import React from 'react';
import cx from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBook, faPlus } from '@fortawesome/pro-regular-svg-icons';
import Panel from 'components/v2/Panel/Panel';
import Chip from 'components/Chip';
import Button, { type ButtonPropsCombined } from 'components/Button/Button';

import './BlankPageWithMockup.scss';

export type BlankPageActionProps = Omit<ButtonPropsCombined, 'ref'> & {
  label: string;
};

export interface BlankPageWithMockupProps {
  className?: string;
  description: React.ReactNode;
  headerText: string;
  chipText?: string;
  mockupImageAlt: string;
  mockupImageHeight?: number;
  mockupImageSrc: string;
  mockupImageWidth?: number;
  primaryActionProps: BlankPageActionProps;
  secondaryActionProps: BlankPageActionProps;
}

const BlankPageWithMockup: React.FC<BlankPageWithMockupProps> = ({
  className,
  description,
  headerText,
  chipText,
  mockupImageAlt,
  mockupImageHeight,
  mockupImageSrc,
  mockupImageWidth,
  primaryActionProps,
  secondaryActionProps,
}) => {
  const trimmedChipText = chipText?.trim();
  const mockupDimensions =
    typeof mockupImageWidth === 'number' &&
    typeof mockupImageHeight === 'number'
      ? { width: mockupImageWidth, height: mockupImageHeight }
      : undefined;

  const {
    label: secondaryLabel,
    icon: secondaryIcon = <FontAwesomeIcon icon={faBook} />,
    ...secondaryRest
  } = secondaryActionProps;

  const {
    label: primaryLabel,
    icon: primaryIcon = <FontAwesomeIcon icon={faPlus} />,
    ...primaryRest
  } = primaryActionProps;

  return (
    <Panel className={cx('blank-page-with-mockup', className)}>
      <div className="blank-page-with-mockup__layout">
        <div className="blank-page-with-mockup__mockup">
          <img
            src={mockupImageSrc}
            alt={mockupImageAlt}
            {...mockupDimensions}
          />
        </div>
        <div className="blank-page-with-mockup__content">
          <div className="blank-page-with-mockup__section blank-page-with-mockup__section--header">
            <h2 className="blank-page-with-mockup__title">{headerText}</h2>
            {trimmedChipText && (
              <Chip color="gray" className="blank-page-with-mockup__chip">
                {trimmedChipText}
              </Chip>
            )}
          </div>
          <div className="blank-page-with-mockup__section blank-page-with-mockup__section--description">
            <div className="blank-page-with-mockup__description">
              {description}
            </div>
          </div>
          <div className="blank-page-with-mockup__section blank-page-with-mockup__section--actions">
            <div className="blank-page-with-mockup__actions">
              <Button
                buttonStyle="outline"
                color="black-tertiary"
                target="_blank"
                icon={secondaryIcon}
                {...secondaryRest}
              >
                {secondaryLabel}
              </Button>
              <Button color="primary" icon={primaryIcon} {...primaryRest}>
                {primaryLabel}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Panel>
  );
};

export default BlankPageWithMockup;
